import { getAllFocusableElements, getNextFocusableElement, getPrevFocusableElement } from '../../utils/tabs';

export default {
  name: 'CommonSelect',
  props: {
    label: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'l',
      validator: (value) => {
        return ['m', 'l'].indexOf(value) !== -1;
      },
    },
    error: Boolean,
    errorMessage: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    required: Boolean,
    disable: Boolean,
    multiple: Boolean,
    options: {
      type: Array,
      default: () => [],
    },
    useInput: Boolean,
    virtualScroll: Boolean,
    pill: Boolean,
    value: {
      type: [String, Object, Array],
      default: null,
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    noParentEvent: {
      type: Boolean,
      default: false,
    },
    /**
     * parentForMenu необходим для того, чтобы в случае,
     * когда select является дочерним составляющим компонтом
     * ширина его dropdownmenu определалась от родителя select
     */
    parentForMenu: {
      type: Object,
      default: null,
      required: false,
    },
    /**
     * свойство inabilityUnselect нужно, чтобы исключить возможность
     * отмены выбора в select, при нажатии на тот же самый option
     */
    inabilityUnselect: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Сообщение при пустом списке опций
     */
    noOptionsLabel: {
      type: String,
      default: 'Elements not found',
    },
    noFit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuOpened: false,
      filter: '',
      filteredOptions: [...this.options],

      tabToMenuEventActive: false,
      ingoreEnterOnSelectActive: false,
    };
  },
  computed: {
    hasValue() {
      if (this.multiple) return !!this.value && this.value.length > 0;
      else return !!this.value;
    },
    visibleValue() {
      if (!this.hasValue) return null;
      if (this.multiple) {
        if (this.isObjectOptions) return this.value.map(i => i[this.optionLabel]).join(', ');
        else return this.value.join(', ');
      } else {
        if (this.isObjectOptions) return this.value[this.optionLabel];
        else return this.value;
      }
    },
    isObjectOptions() {
      return typeof this.options[0] === 'object';
    },
    isFocused() {
      return !this.disable && this.menuOpened;
    },
  },
  watch: {
    multiple: {
      handler(value) {
        this.checkMultiple(value);
      },
    },
    menuOpened(value) {
      this.$emit('open', value);
    },
    filter(value) {
      if (value === undefined) {
        return;
      }
      if (!this.menuOpened && (value !== this.value)) { // while user typing and menu closed
        const event = new Event('click');
        this.$refs.select.dispatchEvent(event); // open it
      }

      this.$emit('filter', value,
        (callback, afterFn) => { // done fn
          callback();

          if (!afterFn) return;
          this.$nextTick(() => {
            afterFn(this.$refs.select);
          });
        },
        // () => { //abort fn

        // }
      );

      if (!this.multiple && (!value || value !== this.value)) {
        this.input(null);
      }

      if (this.$listeners.filter) return;

      this.$set(this, 'filteredOptions', this.options.filter((option) => {
        return this.isObjectOptions
          ? option[this.optionLabel].toLowerCase().includes(value.toLowerCase())
          : option.toLowerCase().includes(value.toLowerCase());
      }));
    },
    value(value) {
      if (this.useInput) {
        if (!value || !value.length) return;

        if (!this.multiple) this.filter = this.isObjectOptions ? value[this.optionLabel] : value;
        else this.filter = '';
      }
    },
    options(value) {
      this.$set(this, 'filteredOptions', [...value]);
    },
  },
  methods: {
    // ---- Управление с клавиатуры
    onEnter(/* e */) {
      // Флаг устанавливается при нажатии enter по опшену.
      // Нужен, чтобы предотвратить всплывание события при смене фокуса
      if (this.ingoreEnterOnSelectActive) {
        this.ingoreEnterOnSelectActive = false;
        return;
      }

      this.toggleMenu();

      // Если используется фильтр, то сразу фокусируемся на него
      if (this.useInput) {
        this.$refs.filterInput.focus();
      }

      // С флагом tabToMenuEventActive, функция onTab будет
      // Перехватывать и отменять стандартный tab.
      // Фокус будет переноситься на меню селекта
      this.tabToMenuEventActive = !this.menuOpened;
    },
    onTab(e) {
      // Флаг устанавливается при нажатии enter по селекту.
      if (!this.tabToMenuEventActive) return;

      // Стандартный таб отменяется, далее просто
      // фокусируемся на нужном элементе через рефы
      e.preventDefault();
      this.tabToMenuEventActive = false;

      let firstOption;
      if (this.virtualScroll) {
        const virtualScroll = this.$refs.virtualScroll;
        const visibleOptions = virtualScroll && virtualScroll._data && virtualScroll._data.childElements;
        const firstOptionWrapper = visibleOptions[0];

        const focusable = getAllFocusableElements(firstOptionWrapper);
        firstOption = focusable && focusable[0];
      } else {
        const scrollArea = this.$refs.scrollArea;
        const options = scrollArea.$slots.default.filter(i => i.elm.nodeType !== 8);
        firstOption = options[0] && options[0].elm;
      }

      // Фокус на первом элементе меню.
      // Далее таб будет перехватываться функцией
      // onOptionTab и переводить на прошлый или следующий
      // focusable элемент (инпут, ссылка и т.д.)
      if (firstOption) {
        firstOption.focus();
      }
    },
    /**
     * Здесь происходит имитация поведения, которое
     * происходило бы при обычном нажатии tab \ shit+tab
     * на элементе селекта. Выбирается следующий или прошлый
     * элемент, на котором возможно сфокусироваться.
     */
    onOptionTab(e) {
      e.preventDefault();
      this.toggleMenu();

      const select = this.$refs.select;
      if (e.shiftKey) {
        // Если таб нажат с шифтом - выбираем основной элемент селекта
        this.forceFocusOnSelect();
      } else {
        // Если таб нажат без шифта - выбираем следующий элемент
        const nextFocusableElement = getNextFocusableElement(select);
        nextFocusableElement.focus();
      }
    },
    /**
     * При нажатии на стрелочку вверх, когда фокус
     * на элементе меню селекта. Перемещает фокус
     * на предыдущий элемент списка
     */
    onOptionUp(e) {
      this.navigateOption(e, 'up');
    },
    /**
     * При нажатии на стрелочку вниз, когда фокус
     * на элементе меню селекта. Перемещает фокус
     * на следующий элемент списка
     */
    onOptionDown(e) {
      this.navigateOption(e, 'down');
    },
    navigateOption(e, direction = 'down') {
      e.preventDefault();

      const { target, parent } = this.getElementsOnOptionNavigate(e);
      const navigateTo = direction === 'down'
        ? getNextFocusableElement(target, parent)
        : getPrevFocusableElement(target, parent);

      if (navigateTo) {
        navigateTo.focus();
        this.scrollToOption(navigateTo, parent);
      }
    },
    /**
     * При нажатии на enter, когда фокус
     * на элементе меню селекта.
     * Выбирает этот элемент как значение
     * и закрывает список (если не multiple)
     */
    onOptionEnter(e) {
      e.preventDefault();
      const target = e.target;
      const option = target && target.__vue__ && target.__vue__._props && target.__vue__._props.option;
      if (!option) return;

      // Выбрать текущий опшен как значение селекта
      this.optionClickHandler({ option });

      if (!this.multiple) {
        this.forceFocusOnSelect();

        // Костыль. При фокусе на селект после нажатия
        // на enter по опшену - срабатывает событие onEnter
        // на селекте. Чтобы этого не было, устанавливаем флаг
        // и обрабатываем его в функции onEnter
        this.ingoreEnterOnSelectActive = true;
      }
    },
    /**
     * При нажатии на escape, когда фокус
     * на элементе меню селекта.
     * Закрывает меню без выбора элемента
     */
    onOptionEsc(/* e */) {
      this.forceFocusOnSelect();
    },
    /**
     * Перевести фокус на главный элемент селекта
     */
    forceFocusOnSelect() {
      this.$nextTick(() => {
        this.$refs.select.focus();
      });
    },
    /**
     * Определить элементы для поиска
     * при нажатии стрелок вниз и вверх по опшену
     */
    getElementsOnOptionNavigate(e) {
      let target;
      let parent;

      if (!this.virtualScroll) {
        target = e.target;
        parent = target.parentElement;
      } else {
        target = e.target;
        parent = this.$refs.virtualScroll.$el;
      }

      return { target, parent };
    },
    /**
     * Скролл до опшена, на который был произведён фокус
     */
    scrollToOption(el, container) {
      // ! TODO Скролл до активного элемента ломает
      // селекты с бесконечным скроллом. Решение найти не смог.

      if (this.virtualScroll) return;
      el.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
    },
    // ---- END Управление с клавиатуры

    toggleMenu() {
      const event = new Event('click');
      this.$refs.select.dispatchEvent(event);
    },
    async openMenuByClick(event) {
      const inputWidth = event.target.clientWidth + 40;
      const inputHeight = event.target.clientHeight + 31;
      if (!this.menuOpened && inputWidth > event.x && event.x > 40 && event.y < inputHeight && event.y > 31) {
        this.toggleMenu();
      }
    },
    async optionClickHandler({ option }) {
      if (this.multiple) {
        const value = this.value ? [...this.value] : [];
        let valueIndex;

        // Найти индекс этого значения в value
        if (this.isObjectOptions) {
          valueIndex = value.findIndex((i) => i.value === option[this.optionValue]);
        } else {
          valueIndex = value.findIndex((i) => i === option);
        }

        // Если не найден, то добавить. Иначе удалить значение
        if (valueIndex === -1) {
          value.push(option);
        } else {
          value.splice(valueIndex, 1);
        }
        this.input(value);
      } else {
        if (
          ((!!this.value && this.value === option) ||
          (this.value && !!this.value[this.optionValue] &&
          this.value[this.optionValue] === option[this.optionValue])) &&
          !this.inabilityUnselect
        ) {
          this.input(null);
        } else {
          this.input(option);

          this.filter = undefined; // clear input
        }

        this.menuOpened = false;
      }
    },
    checkMultiple(multiple) {
      if (multiple) {
        if (this.value) this.input([this.value]);
        else this.input(null);
      } else {
        if (this.value && this.value.length > 0) this.input(this.value[0]);
        else this.input(null);
      }
    },
    input(value) {
      this.$emit('input', value);
    },
    onFilterKeydown(evt) {
      if (this.filter) return;
      if (!this.hasValue) return;

      const key = evt.keyCode || evt.charCode;

      if (key === 8 || key === 46) {
        if (this.multiple) {
          this.value.pop();
        } else {
          this.input(null);
        }
        const event = new Event('click');
        // relocate menu
        this.$refs.select.dispatchEvent(event);
        this.$refs.select.dispatchEvent(event);
      }
    },
    menuToggle() {
      if (this.noParentEvent) this.menuOpened = !this.menuOpened;
    },
  },
};
